import React, { useState } from "react";
import { Text, TextTitle } from "./Texts";

import Column from "./Column";
import DropdownCheckBox from "./DropdownCheckBox";
import Icon from "./Icon";
import PropTypes from "prop-types";
import Row from "./Row";
import TablePagination from "./TablePagination";
import _cloneDeep from "lodash/cloneDeep";
import _find from "lodash/find";
import _get from "lodash/get";
import styled from "styled-components";
import useWindowSize from "../layout/useWindowSize";
import colors from "../styles/colors";
import { Trans, useTranslation } from "react-i18next";

const Table = ({
  columns = [],
  data = [],
  responsiveColumns = 1,
  freezeColumns = 1,
  maxHeight,
  pagination,
  sortOptions,
}) => {
  const isMobile = useWindowSize().width < 768;
  const isEmpty = !data.length || (data.length === 1 && typeof _find(data, { isTotal: true }) !== "undefined");
  console.log("TABLE", isEmpty, data);
  if (isMobile && columns.length > 3) {
    return (
      <>
        <TableMobile
          columns={columns}
          data={data}
          responsiveColumns={responsiveColumns}
          freezeColumns={freezeColumns}
          isEmpty={isEmpty}
          sortOptions={sortOptions}
        />
        {!isEmpty && pagination && pagination.total ? (
          <TablePagination
            onPageChange={pagination.onPageChange}
            total={pagination.total}
            size={pagination.size}
          ></TablePagination>
        ) : null}
      </>
    );
  } else {
    return (
      <>
        <StyledTable maxHeight={maxHeight}>
          <TableHeader columns={columns} isEmpty={isEmpty} sortOptions={sortOptions}></TableHeader>
          {isEmpty ? (
            <EmptyContent />
          ) : (
            <>
              <div className="row-container">
                {data.map((row, index) => (
                  <TableRow key={index} columnData={columns} rowData={row}></TableRow>
                ))}
              </div>
              {pagination && pagination.total ? (
                <TablePagination
                  onPageChange={pagination.onPageChange}
                  total={pagination.total}
                  size={pagination.size}
                ></TablePagination>
              ) : null}
            </>
          )}
        </StyledTable>
      </>
    );
  }
};

Table.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  responsiveColumns: PropTypes.number,
  freezeColumns: PropTypes.number,
  maxHeight: PropTypes.string,
  sortOptions: PropTypes.object,
};

const EmptyContent = () => {
  const [t] = useTranslation();
  return (
    <StyledEmptyContainer>
      <p>{t("__no_data")}</p>
    </StyledEmptyContainer>
  );
};

const TableMobile = ({ columns, data, responsiveColumns, freezeColumns, isEmpty, sortOptions }) => {
  const [t] = useTranslation();
  const [mobileColumns = [], setMobileColumns] = useState();
  const clonedColumns = _cloneDeep(columns);
  const dropdownItems = clonedColumns.slice(freezeColumns).map((column) => {
    return {
      title: column.title,
      value: column.key,
    };
  });
  const activeColumns =
    mobileColumns.length > 0 ? _cloneDeep(mobileColumns) : dropdownItems.slice(0, responsiveColumns);

  const generateMobileColumn = (selectedDropdownItem) => {
    return _find(clonedColumns, { key: selectedDropdownItem.value });
  };

  const onMobileColumnSelected = (items) => {
    console.log("onMobileColumnSelected", items);
    setMobileColumns(items);
  };

  const modifiedColumns = clonedColumns.slice(0, freezeColumns).map((column) => {
    if (!_get(column.options, "isSymbol")) {
      column.options.width = "30%";
    }
    return column;
  });

  if (modifiedColumns.length === 1 && responsiveColumns === 1) {
    modifiedColumns.push(clonedColumns[1]);
    modifiedColumns.push(generateMobileColumn(mobileColumns.length > 1 ? mobileColumns[1] : dropdownItems[1]));
  } else {
    for (var i = 0; i < responsiveColumns; i++) {
      modifiedColumns.push(generateMobileColumn(mobileColumns.length > i ? mobileColumns[i] : dropdownItems[i]));
    }
  }

  console.log("modifiedColumns", modifiedColumns);

  return (
    <StyledTable>
      {!isEmpty && (
        <SortContainer>
          <Text>{t("__display")}</Text>
          <DropdownCheckBox
            items={dropdownItems}
            minWidth="150px"
            selectedItems={activeColumns}
            maxSelection={responsiveColumns}
            onApply={onMobileColumnSelected}
          />
        </SortContainer>
      )}
      <TableHeader columns={modifiedColumns} isEmpty={isEmpty} sortOptions={sortOptions}></TableHeader>
      {isEmpty ? (
        <EmptyContent />
      ) : (
        data.map((row, index) => <TableRow key={index} columnData={modifiedColumns} rowData={row}></TableRow>)
      )}
    </StyledTable>
  );
};

/*
  column = {
    key: string,
    title: string || Component,
    options: {
      width: string (optional),
      align: string (optional)
    },
  },
  sortOptions = {
    key: string,
    direction: string,
    onSelected: function
  }
 */
const TableHeader = ({ columns, isEmpty, sortOptions }) => {
  const sortable = typeof sortOptions === "object";
  console.log("sortOptions", sortOptions);
  const columnItems = columns.map((info, index) => {
    const options = info.options || {};
    let title = info.title;
    if (typeof title !== "object" && sortable && !isEmpty) {
      const onClick = () => {
        if (info.key === sortOptions.key) {
          sortOptions.onSelected({ key: info.key, direction: sortOptions.direction === "asc" ? "desc" : "asc" });
        } else {
          sortOptions.onSelected({ key: info.key, direction: "desc" });
        }
      };
      title = (
        <Row alignItems="center" columnSpacing="2px" onClick={onClick}>
          <Column>
            <TextTitle
              size={options.strongText && !sortable ? "14px" : "12px"}
              color={isEmpty ? colors.descriptionText : colors.text}
              ellipsis
              tooltip
            >
              {info.title}
            </TextTitle>
          </Column>
          <Column className="sorter" wrap>
            <Icon
              name="icon-sort-up"
              color={sortOptions.key === info.key && sortOptions.direction === "asc" ? colors.mainText : colors.subText}
            />
            <Icon
              name="icon-sort-down"
              color={
                sortOptions.key === info.key && sortOptions.direction === "desc" ? colors.mainText : colors.subText
              }
            />
          </Column>
        </Row>
      );
    } else if (typeof title !== "object") {
      title = (
        <TextTitle
          size={options.strongText && !sortable ? "14px" : "12px"}
          color={isEmpty ? colors.descriptionText : colors.text}
          ellipsis
          tooltip
        >
          {info.title}
        </TextTitle>
      );
    }
    return (
      <Column
        key={info.key}
        width={options.width}
        align={index === 0 ? "left" : options.align}
        wrap={options.width ? "wrap" : ""}
      >
        {title}
      </Column>
    );
  });
  return <StyledHeader columnSpacing="16px">{columnItems}</StyledHeader>;
};

/*
  rowData = {
    key: value,
    key1: value2,
    keyN: valueN
  }
 */
const TableRow = ({ columnData, rowData }) => {
  const rowItems = columnData.map((column, index) => {
    const options = column.options || {};
    var text = <Text size={options.fontSize ? options.fontSize : "12px"}>{rowData[column.key]}</Text>;
    if (typeof rowData[column.key] === "object") {
      text = rowData[column.key];
    } else if (rowData.isTotal) {
      if (index === 0) {
        text = (
          <Row columnSpacing="4px" alignItems="baseline" responsive>
            <Trans i18nKey="__total_column" count={columnData[0].title}>
              <TextTitle size={"14px"}>{rowData[column.key]}</TextTitle>
              <Text>{columnData[0].title}</Text>
            </Trans>
          </Row>
        );
      } else {
        text = <TextTitle size={"14px"}>{rowData[column.key]}</TextTitle>;
      }
    } else if (options.strongText) {
      text = <TextTitle size={options.fontSize ? options.fontSize : "12px"}>{rowData[column.key]}</TextTitle>;
    }

    return (
      <Column key={index} width={options.width} align={options.align} wrap={options.width ? "wrap" : ""}>
        {text}
      </Column>
    );
  });
  return <StyledRow columnSpacing="16px">{rowItems}</StyledRow>;
};

export default Table;

const StyledTable = styled.div.attrs((props) => ({
  maxHeight: props.maxHeight,
  overflow: props.maxHeight ? "scroll" : "auto",
}))`
  > .row-container {
    max-height: ${(props) => props.maxHeight};
    overflow: ${(props) => props.overflow};
  }
`;

const StyledHeader = styled(Row).attrs((props) => ({}))`
  border-bottom: 2px solid #dedede;
  padding: 16px;
  align-items: baseline;

  & .column {
    display: flex;
    align-items: center;
    flex-grow: 0;

    i {
      margin-right: -8px;
    }
  }

  .sorter {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0 -8px 0 0;
    cursor: pointer;

    i {
      margin: -10px 0;
      font-size: 14px;
    }
  }
`;

const StyledRow = styled(Row).attrs((props) => ({}))`
  border-bottom: 0.5px solid #dedede;
  padding: 16px;
  align-items: baseline;
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledEmptyContainer = styled.div`
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.descriptionText};
`;
